<script lang="ts" setup>
    import '../../../css/choice.css';
    import {onMounted,ref,computed,reactive} from 'vue';  
    import {popAlert} from '../../lib/Notification'; 
    import {ButtonDisplaySize,ButtonDisplayStyle,UserPinType,DisplayStyle, IconTypes} from '../../Enums/UiEnums';
    import { setDataQueryComponentId} from "../../lib/DataQueryHelper" ;
    import {IPatientRxQueueResponse,IInsuranceSearch,IPrescriptionStatus,RxQueueStatus,
        GetPatientRxQueue,minimum_signing_credential_type,ApprovalType,
        SignSendInputs,SendPrescriptionQueue,ISendPrescriptionResult,
        SignPrescriptionQueue,ISignPrescriptionResult,IPrescriptionSearch} from '../../lib/Models/PatientRxModel';
    import { severityLevel} from '../../lib/Models/InteractionModel';
    import PatientInformation from "../../components/PatientInformation.vue"; 
    import PrescriberInformation from "../../components/PrescriberInformation.vue";
    import PrescriptionInformation from "../../components/PrescriptionInformation.vue";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import {ActionButtonStyle} from '../../lib/Types/Types';
    import {NumberToLocaleString} from '../../lib/Formatting/Number'; 
    import DataQueryMessage from "../../components/Communication/DataQueryMessage.vue"; 
    import RxPin from "../../components/Security/RxPin.vue"
    import ModalDialog from '../../components/ModalDialog.vue'; 
    import CredentialSignForIdme from '../../ChoiceScreens/Credentials/CredentialSignForIdMe.vue'; 
    import Icon from "../../components/Icon.vue"; 
 
    enum RxSelectedType {
        Provider = 0,
        Scheduled = 1,
        NonScheduled = 2,
        All = 3,
        Interaction = 4
    };     
    const dataQuerySetup = setDataQueryComponentId();
    const dataQueryComm = ref<typeof DataQueryMessage>();
    const rxPrescriptionData = ref<IPrescriptionStatus[]>();  
    const validPatient = ref<boolean>(false); 
    const loadingPatient = ref<boolean>(false); 
    const formData = reactive <
    {   
        currentID: number,
        completed: ApprovalType,
        cancelButton : {Label : string, Tooltip : string},
        rxType: RxSelectedType,
        window: {headerHeight: number, footerHeight: number, windowPadding: number, workareaHeight: number},
        signedCount: {provider : number, scheduled: number, nonScheduled: number, disableScheduled: boolean, disableNonScheduled: boolean},
        prescriptionCount: {provider: number, nonProvider: number, scheduled: number, nonScheduled: number, interaction: number}
        rxQueueResults? : IPatientRxQueueResponse,
        showPIN: boolean,
        sentPrescriptionList? : ISendPrescriptionResult[],
        signPrescriptionErrorList? : ISignPrescriptionResult[],
        signPrescriptionList : string[] ,
        showSentPrescriptions: boolean,
        showSignErrorPrescriptions: boolean,
        displaySigning: boolean,
        queueSigning: boolean
    }>({     
        currentID: 0,
        completed: ApprovalType.Pending,
        cancelButton : {Label : "Cancel", Tooltip : "Cancel Approval Process, prescriptions will be left in the status as you see them now."},
        rxType: RxSelectedType.Provider,
        window: {headerHeight: 5.5, footerHeight: 3, windowPadding: 3.5, workareaHeight: 5},
        signedCount: {provider : 0, scheduled: 0, nonScheduled: 0, disableScheduled: true, disableNonScheduled: true},
        prescriptionCount: {provider: 0, nonProvider: 0, scheduled: 0, nonScheduled: 0, interaction: 0},
        showPIN: false,
        showSentPrescriptions: false,
        showSignErrorPrescriptions: false,
        displaySigning: false,
        signPrescriptionList: [],
        queueSigning: true
    });
    const props = withDefaults(defineProps<{
        saveEnabled?: number,
        patientId: number,
        patientType?: string,
        rxQueueResults: IPatientRxQueueResponse
    }>(),
    {
        saveEnabled: 0,  
        patientType: "Patient"
    });

    onMounted(async () => {
        formData.rxQueueResults = props.rxQueueResults;
        resetCountsandResults();
        resetHeights(); 
        window.addEventListener('resize', () => {resetHeights(); });
        getNextPrescriptionID(-1); 
    });  

    function resetCountsandResults() : void {
        validPatient.value = false;
        loadingPatient.value = false;
        formData.prescriptionCount = {provider: 0, nonProvider: 0, scheduled: 0, nonScheduled: 0, interaction: 0};
        rxPrescriptionData.value = undefined; 
        if ((formData.rxQueueResults?.prescriptionList) && (formData.rxQueueResults.prescriptionList.length !== 0)) {
            validPatient.value = ((formData.rxQueueResults.patientData.patientID ?? 0) > 0 );
            rxPrescriptionData.value = formData.rxQueueResults.prescriptionList.map(item => ({PrescriptionID: item.prescriptionID, QueueStatusID: item.queueStatusID, ScheduleLevel: item.scheduleLevel, ProviderAccess: item.providerAccess, Interaction: determineRxSeverity(item.allergySeverityLevel ?? 0,item.drugSeverityLevel ?? 0,item.diseaseSeverityLevel ?? 0)})); 
            formData.prescriptionCount.provider = formData.rxQueueResults.prescriptionList.filter(item => item.providerAccess === 1).length;
            formData.prescriptionCount.nonProvider= formData.rxQueueResults.prescriptionList.filter(item => item.providerAccess === 0).length;
            formData.prescriptionCount.scheduled = formData.rxQueueResults.prescriptionList.filter(item => item.providerAccess === 1 && item.scheduleLevel !== 0).length;
            formData.prescriptionCount.nonScheduled = formData.rxQueueResults.prescriptionList.filter(item => item.providerAccess === 1 && item.scheduleLevel === 0).length; 
            formData.prescriptionCount.interaction = formData.rxQueueResults.prescriptionList.filter(item => (item.allergySeverityLevel ?? severityLevel.None) > severityLevel.None || (item.diseaseSeverityLevel ?? severityLevel.None) > severityLevel.None || (item.drugSeverityLevel ?? severityLevel.None) > severityLevel.None).length; 
        } 
        else if (formData.rxQueueResults?.patientData) {
            validPatient.value = ((formData.rxQueueResults.patientData.patientID ?? 0) > 0 );
        }        
        if (formData.prescriptionCount.provider === 0) {
            formData.rxType = RxSelectedType.All;
        }
        setReadyToSignCounts();
    } 

    function resetHeights(): void {
        const windowHeight = window.innerHeight / 16;
        formData.window.workareaHeight = windowHeight - formData.window.headerHeight - formData.window.footerHeight - formData.window.windowPadding;
    }

    const primaryInsurance = computed<IInsuranceSearch | undefined>(() => {
        if (!formData.rxQueueResults) {
            return;
        }
        if ((!formData.rxQueueResults.insuranceList) || (formData.rxQueueResults.insuranceList.length === 0))
            return; 
        return formData.rxQueueResults.insuranceList[0];     
    });
    const primaryInsuranceData = computed<string | undefined>(() => { 
        return `${primaryInsurance.value?.coverageLevel ?? ""} ${primaryInsurance.value?.payerName ?? ""} ${primaryInsurance.value?.payerPhone ?? ""}`;
    });    
    const secondaryInsurance = computed<IInsuranceSearch | undefined>(() => {
        if (!formData.rxQueueResults) {
            return;
        }
        if ((!formData.rxQueueResults.insuranceList) || (formData.rxQueueResults.insuranceList.length < 2))
            return; 
        return formData.rxQueueResults.insuranceList[1];     
    });
    const secondaryInsuranceData = computed<string | undefined>(() => { 
        return `${secondaryInsurance.value?.coverageLevel ?? ""} ${secondaryInsurance.value?.payerName ?? ""} ${secondaryInsurance.value?.payerPhone ?? ""}`;
    });
    const prescriberExists = computed<boolean>(() => {
        if (!formData.rxQueueResults) {
            return false;
        }
        if ((!formData.rxQueueResults.prescriberList) || (formData.rxQueueResults.prescriberList.length === 0))    
            return false;
        return true;
    });  
    
    const prescriptionExists = computed<boolean>(() => {
        if (!formData.rxQueueResults) {
            return false;
        }
        if ((!formData.rxQueueResults.prescriptionList) || (formData.rxQueueResults.prescriptionList.length === 0))    
            return false;
        return true;
    });  
    async function getPIN() : Promise<void>  {
        formData.showPIN = true;
    }

    async function signPrescriptions() : Promise<void>  {
        if (!formData.rxQueueResults) {
            return;
        }
        if (formData.rxQueueResults.credentialData.credentialTypeID < minimum_signing_credential_type) {
            popAlert("At this time, your credential type does not allow you to sign queued prescriptions.","Sign Prescriptions");
            return;
        }
        if (!rxPrescriptionData.value) {
            return;
        }
        if (formData.signedCount.scheduled === 0) {
            return;
        }
        
        formData.signPrescriptionList = [];
        formData.signPrescriptionErrorList = undefined
        const queueRecords =  rxPrescriptionData.value.filter(item => item.QueueStatusID === RxQueueStatus.ReadyToSign && item.ProviderAccess === 1 && item.ScheduleLevel !== 0).map(item => ({prescriptionId: item.PrescriptionID}));
        const rx_inputs : SignSendInputs = {SignSendRecords : queueRecords}; 
        const respSave = await SignPrescriptionQueue(rx_inputs); 
        
        if (respSave.error) {  
            popAlert(respSave.error.error ?? "Unknown Error","Sign Prescription");
            return;
        }

        if (respSave.success?.prescriptionList) {
            formData.signPrescriptionList = respSave.success?.prescriptionList.filter(item => item.errorCode === 0).map(item => item.epcsInformation).flat();
            formData.signPrescriptionErrorList = respSave.success?.prescriptionList.filter(item => item.errorCode !== 0);
            if (formData.signPrescriptionErrorList.length > 0) {
                formData.showSignErrorPrescriptions = true;
                return; 
            }
            if (formData.signPrescriptionList.length > 0) { 
                showSigning();
            }
        } 
    }   
    async function sendPrescriptions() : Promise<void>  {
        if (!rxPrescriptionData.value) {
            return;
        }
        if (formData.signedCount.nonScheduled === 0) {       
            return;
        }
        formData.sentPrescriptionList = undefined;
        const queueRecords = rxPrescriptionData.value.filter(item => item.QueueStatusID === RxQueueStatus.ReadyToSign && item.ProviderAccess === 1 && item.ScheduleLevel === 0).map(item => ({prescriptionId: item.PrescriptionID}));
        const rx_inputs : SignSendInputs = {SignSendRecords : queueRecords};
        const respSave = await SendPrescriptionQueue(rx_inputs);   
        
        if (respSave.error) {  
            popAlert(respSave.error.error ?? "Unknown Error","Send Prescription");
            return;
        } 
        if (respSave.success?.prescriptionList) {
            if (respSave.success.prescriptionList.length > 0) {
                formData.sentPrescriptionList = respSave.success?.prescriptionList;
                formData.showSentPrescriptions = true; 
            }
        }
    }

    async function cancelProcess() : Promise<void>  {
        if (dataQuerySetup.isUsed) {
            dataQueryComm.value?.closeDataQuery({dataQueryComponentId: dataQuerySetup.componentID});
            return;
        }        
        window.close();
    }  

    function rxTypeClick(newType : RxSelectedType) : void {
        formData.rxType = newType;
    }

    function reloadPatientInfo(): void
    {
        loadNewPatient(formData.rxQueueResults?.patientData.patientID ?? -1,"Patient");
    }

    function determineRxSeverity(allergySeverityLevel: severityLevel, drugSeverityLevel: severityLevel, diseaseSeverityLevel: severityLevel) : boolean {
        if (allergySeverityLevel > severityLevel.None) 
            return true;
        if (drugSeverityLevel > severityLevel.None) 
            return true;
        if (diseaseSeverityLevel > severityLevel.None) 
            return true;
        return false;
    }

    function getNextPrescriptionID(currentIndex : number) : void {
        if (rxPrescriptionData.value) { 
            for (let i = 0;i < 2;i++) {
                let nextRxList = rxPrescriptionData.value.filter((item,index) => (index > currentIndex && item.ProviderAccess === 1 && item.QueueStatusID === RxQueueStatus.Pending));
                if (nextRxList.length > 0) {
                    resetCurrentId(nextRxList[0].PrescriptionID); 
                    break;
                }
                currentIndex = -1; 
            }
        }
    }

    function resetCurrentId(currentPrescriptionID : number) : void { 
        formData.currentID = currentPrescriptionID;
    }

    function resetPrescriptionStatus(newStatus : RxQueueStatus,index : number, prescriptionID : number) : void {  
        if (rxPrescriptionData.value) { 
            formData.currentID = prescriptionID;
            const search_index = rxPrescriptionData.value.findIndex(item => item.PrescriptionID === prescriptionID);
            if (search_index != -1) {
                rxPrescriptionData.value[search_index].QueueStatusID = newStatus;
                setReadyToSignCounts();
                getNextPrescriptionID(index);
            }
        }
    }

    function setReadyToSignCounts() : void { 
        formData.signedCount = {provider : 0, scheduled: 0, nonScheduled: 0, disableScheduled: true, disableNonScheduled: true};
        if (rxPrescriptionData.value) {
            formData.signedCount.provider = rxPrescriptionData.value.filter(item => item.QueueStatusID === RxQueueStatus.ReadyToSign && item.ProviderAccess === 1).length;
            formData.signedCount.scheduled = rxPrescriptionData.value.filter(item => item.QueueStatusID === RxQueueStatus.ReadyToSign && item.ProviderAccess === 1 && item.ScheduleLevel !== 0).length;
            formData.signedCount.nonScheduled = rxPrescriptionData.value.filter(item => item.QueueStatusID === RxQueueStatus.ReadyToSign && item.ProviderAccess === 1 && item.ScheduleLevel === 0).length;
        }
        formData.signedCount.disableScheduled = (formData.signedCount.scheduled === 0);
        formData.signedCount.disableNonScheduled = (formData.signedCount.nonScheduled === 0);
    }

    function showProvider(providerAccess : number) : boolean {
        if (formData.rxType === RxSelectedType.All)
            return true;
        if (providerAccess !== 1) 
            return false; 
        return true; 
    }

    function showPrescription(rxRecord : IPrescriptionSearch) : boolean { 
        if (!rxRecord)
            return false;
        if (rxRecord.queueStatusID === RxQueueStatus.Denied) 
            return false;
        if (formData.rxType === RxSelectedType.All)
            return true;           
        if (rxRecord.providerAccess  !== 1)
            return false;            
        if ((formData.rxType === RxSelectedType.Interaction) && ((rxRecord.allergySeverityLevel ?? severityLevel.None) > severityLevel.None))
            return true;                    
        if ((formData.rxType === RxSelectedType.Interaction) && ((rxRecord.diseaseSeverityLevel ?? severityLevel.None) > severityLevel.None))
            return true;                    
        if ((formData.rxType === RxSelectedType.Interaction) && ((rxRecord.drugSeverityLevel ?? severityLevel.None) > severityLevel.None))
            return true;      
        if (formData.rxType === RxSelectedType.Provider)
            return true;
        if (formData.rxType === RxSelectedType.Scheduled && rxRecord.scheduleLevel !== 0)         
            return true;
        if (formData.rxType === RxSelectedType.NonScheduled && rxRecord.scheduleLevel === 0)         
            return true;
       return false; 
    } 
    function openDataQueryView(payload: any) : void {
        if ("data" in payload) {
            if (("screenColumnValue" in payload.data) && ("SearchID" in payload.data)) {
                validPatient.value = false;
                loadingPatient.value = true;
                formData.showPIN = false;
                formData.displaySigning = false;
                formData.completed = (payload.data.SearchID == 1 ? ApprovalType.Pending : ApprovalType.Completed);
                if (formData.completed === ApprovalType.Completed) {
                    formData.cancelButton = {Label: "Close", Tooltip: "Close Window"};
                }
                loadNewPatient(payload.data.screenColumnValue,"Patient");
            }
        }
    }
    async function loadNewPatient(patientId : number, patientType: string) : Promise<void>  {
        const respGet = await GetPatientRxQueue(patientId,patientType,formData.completed);
        if (!respGet.success) {  
            popAlert(respGet.error?.error ?? "Unable to retrieive patient information.","Refresh Totals");
            return;
        }  
        formData.rxQueueResults = respGet.success; 
        resetCountsandResults();
    }
    function closePin(pinValidated : boolean) : void { 
        formData.showPIN = false; 
        if (pinValidated) {
            sendPrescriptions();
        }
    }
    function hideSentPrescriptions() : void {
        formData.showSentPrescriptions = false;
        reloadPatientInfo();
    }
    function hideSignErrorPrescriptions() : void {
        formData.showSignErrorPrescriptions = false;
    }  
    function closeSigning(signingMessage : string) { 
        hideSigning();
        if (signingMessage === "Approved") {
            reloadPatientInfo();
        }
    } 
    async function hideSigning() : Promise<void>  {
        formData.displaySigning = false;
    }
    function showSigning() : void {
        formData.displaySigning = true;
    }
</script> 
<template> 
    <div v-show="!formData.displaySigning">
        <div class="row" :style="{ 'overflow': 'hidden', 'height': (formData.window.headerHeight + 'rem')}">
            <div v-show="validPatient" class="col-auto label-isalus-small text-primary">
                <PatientInformation
                    :patientID="formData.rxQueueResults?.patientData.patientID ?? -1"  
                    :patientFullName="formData.rxQueueResults?.patientData.patientFullName ?? ''"
                    :birthDate="formData.rxQueueResults?.patientData?.birthDate"  
                    :gender="formData.rxQueueResults?.patientData?.gender"  
                    :patientAddress="formData.rxQueueResults?.patientData?.patientAddress" 
                    :patientCity="formData.rxQueueResults?.patientData?.patientCity" 
                    :patientState="formData.rxQueueResults?.patientData?.patientState" 
                    :patientZipcode="formData.rxQueueResults?.patientData?.patientZipCode" 
                    :homePhone="formData.rxQueueResults?.patientData?.homePhone"  
                    :workPhone="formData.rxQueueResults?.patientData?.workPhone"  
                    :otherPhone="formData.rxQueueResults?.patientData?.otherPhone" 
                    :primaryInsuranceData="primaryInsuranceData"
                    :secondaryInsuranceData="secondaryInsuranceData" />
            </div> 
            <div v-show="!validPatient && loadingPatient" class="col-auto fs-6 text-primary">
                Loading Patient Prescription Queue ....
            </div>
            <div v-show="!validPatient && !loadingPatient" class="col-auto fs-6 text-danger">
                Invalid Patient, no queue information available.
            </div>
        </div>  
        <div v-show="validPatient" class="pt-2 pb-2" v-if="prescriberExists && prescriptionExists"> 
            <div class="row border-top pt-2"> 
                <div class="col skinnyscroll " :style="{'overflow-y': 'scroll', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight + 'rem')}">  
                    <div v-for="prescriberRecord in formData.rxQueueResults?.prescriberList">  
                        <div class="row mt-2" v-show="showProvider(prescriberRecord.providerAccess)"> 
                            <div class="col-auto label-isalus-small text-primary">
                                <PrescriberInformation 
                                    :prescriberID="prescriberRecord.prescriberID"
                                    :providerID="prescriberRecord.billingProviderID"
                                    :providerFullName="prescriberRecord.providerFullName"
                                    :prescriberDEA="prescriberRecord.dea"
                                    :prescriberNPI="prescriberRecord.npi"
                                    :prescriberLocation="prescriberRecord.prescriberLocation"
                                    :prescriberAddress="prescriberRecord.prescriberAddress"
                                    :prescriberCity="prescriberRecord.prescriberCity"
                                    :prescriberState="prescriberRecord.prescriberState"
                                    :prescriberZipcode="prescriberRecord.prescriberZipcode"
                                    :providerAccess="prescriberRecord.providerAccess"
                                />
                            </div>   
                        </div>
                        <div v-for="(rxRecord,index) in formData.rxQueueResults?.prescriptionList?.filter(item => item.prescriberID == prescriberRecord.prescriberID)" :key="rxRecord.prescriptionID"> 
                        <div class="row mt-2" v-show="showPrescription(rxRecord)">
                                <div class="col-auto label-isalus-small text-primary">
                                    <PrescriptionInformation 
                                        :index="index"
                                        :completed="formData.completed"
                                        :patientID="formData.rxQueueResults?.patientData.patientID ?? -1"
                                        :prescriberID="rxRecord.prescriberID"
                                        :prescriptionID="rxRecord.prescriptionID"
                                        :scheduleLevel="rxRecord.scheduleLevel"
                                        :writtenDateUTC="rxRecord.writtenDateUTC"
                                        :effectiveDate="rxRecord.effectiveDate"
                                        :voidAfterDate="rxRecord.voidAfterDate"
                                        :drugName="rxRecord.drugName" 
                                        :fullSig="rxRecord.fullSig"
                                        :fullDispense="rxRecord.fullDispense"
                                        :refillDescription="rxRecord.refillDescription"
                                        :dispenseAsWritten="rxRecord.dispenseAsWritten"
                                        :pharmacyInformation="rxRecord.pharmacyInformation"
                                        :pharmacyPhone="rxRecord.pharmacyPhone"
                                        :pharmacyFax="rxRecord.pharmacyFax"
                                        :queueStatusCreation="rxRecord.queueStatusCreation"
                                        :queueStatus="rxRecord.queueStatusID"
                                        :saveEnabled="props.saveEnabled"
                                        :credentialTypeID = "formData.rxQueueResults?.credentialData.credentialTypeID ?? 0"
                                        :providerFullName="rxRecord.providerFullName"
                                        :providerAccess="rxRecord.providerAccess"
                                        :drugSeverityLevel="rxRecord.drugSeverityLevel"
                                        :allergySeverityLevel="rxRecord.allergySeverityLevel"
                                        :diseaseSeverityLevel="rxRecord.diseaseSeverityLevel"
                                        :rxStatusCode="rxRecord.rxStatusCode"
                                        :currentId="formData.currentID"
                                        @change-rx-status="resetPrescriptionStatus"
                                    />
                                </div>
                            </div>  
                        </div>
                    </div> 
                </div>
            </div>
        </div>
        <div class="row border-top pt-2" :style="{'overflow': 'hidden', 'height': (formData.window.footerHeight + 'rem')}"> 
            <div v-show="validPatient" class="col-8 text-start" >
                <div class="btn-group" role="group" aria-label="Basic radio toggle button group">
                    <label v-if="formData.prescriptionCount.provider > 0" class="btn btn-outline-primary" title="Display only the prescriptions that can be prescribed by you." style="font-size: .75rem;" >
                        Provider
                        <input v-if="formData.prescriptionCount.provider > 0" type="radio" class="btn-check" name="rxType" autocomplete="off" checked @change="rxTypeClick(RxSelectedType.Provider)">
                    </label>

                    <label v-if="formData.prescriptionCount.nonScheduled > 0 && formData.prescriptionCount.scheduled > 0" class="btn btn-outline-primary" title="Display only the scheduled prescriptions that can be prescribed by you."  style="font-size: .75rem;">
                        <input v-if="formData.prescriptionCount.nonScheduled > 0 && formData.prescriptionCount.scheduled > 0" type="radio" class="btn-check" name="rxType" autocomplete="off" @change="rxTypeClick(RxSelectedType.Scheduled)">
                        Scheduled
                    </label>
                    
                    <label v-if="formData.prescriptionCount.nonScheduled > 0 && formData.prescriptionCount.scheduled > 0" class="btn btn-outline-primary" title="Display only the non-scheduled prescriptions that can be prescribed by you."  style="font-size: .75rem;" >
                        <input v-if="formData.prescriptionCount.nonScheduled > 0 && formData.prescriptionCount.scheduled > 0" type="radio" class="btn-check" name="rxType"  autocomplete="off" @change="rxTypeClick(RxSelectedType.NonScheduled)">
                        Non-scheduled
                    </label>
                    
                    <label v-if="formData.prescriptionCount.interaction > 0" class="btn btn-outline-primary" title="Display only prescriptions with interactions."  style="font-size: .75rem;">
                        <input v-if="formData.prescriptionCount.interaction > 0" type="radio" class="btn-check" name="rxType" autocomplete="off" @change="rxTypeClick(RxSelectedType.Interaction)">
                        Interactions
                    </label>
                    
                    <label v-if="formData.prescriptionCount.nonProvider > 0" class="btn btn-outline-primary" title="Display all prescriptions in the patient's queue."  style="font-size: .75rem;" >
                        <input v-if="formData.prescriptionCount.nonProvider > 0" type="radio" class="btn-check" name="rxType" autocomplete="off" @change="rxTypeClick(RxSelectedType.All)">
                        All
                    </label>
                </div>

                <button type="button" class="btn btn-outline-primary ms-4" title="Reload the patient's queue from the database." style="font-size: .75rem;" @click="reloadPatientInfo">
                    Reload
                </button>
            </div> 
            <div :class="(validPatient ? 'col-4 text-end' : 'col text-end')"> 
                <span v-if="props.saveEnabled === 1 && validPatient && formData.completed === ApprovalType.Pending" class="pe-2">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Save"  
                        :text="`Sign (${NumberToLocaleString(formData.signedCount.scheduled)})`"  
                        :tooltip="`Sign ${NumberToLocaleString(formData.signedCount.scheduled)} scheduled prescription(s).`"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                        :spinText="'Signing'"
                        :performAction="signPrescriptions" 
                        :disabled="formData.signedCount.disableScheduled" />
                </span>
                <span v-if="props.saveEnabled === 1 && validPatient && formData.completed === ApprovalType.Pending" class="pe-2">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Save"  
                        :text="`Send (${NumberToLocaleString(formData.signedCount.nonScheduled)})`"  
                        :tooltip="`Send ${NumberToLocaleString(formData.signedCount.nonScheduled)} non-scheduled prescription(s).`"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                        :spinText="'Sending'"
                        :performAction="getPIN" 
                        :disabled="formData.signedCount.disableNonScheduled" />
                </span>
                <ActionButtonV2
                    :action="ActionButtonStyle.Close"  
                    :text="formData.cancelButton.Label"  
                    :tooltip="formData.cancelButton.Tooltip"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                    :performAction="cancelProcess" /> 
            </div> 
        </div>
        <div v-if="dataQuerySetup.isUsed">
            <DataQueryMessage ref="dataQueryComm" :dataQueryParent="false" :componentID="dataQuerySetup.componentID" @openDataQuery="openDataQueryView" /> 
        </div> 
    </div> 
    <ModalDialog v-model:show="formData.showSentPrescriptions" @hide="hideSentPrescriptions">
        <template #title>
            <div class="text-isalus-primary">Sent Prescriptions </div>
        </template>
        <div class="label-isalus-small text-isalus-primary">
            Below is the status of each prescription you have marked to be sent, items marked in red were unable to be sent:
        </div>
        <br/>
        <table>
            <tr>
                <td class="label-isalus-small pe-2">#</td>
                <td class="label-isalus-small pe-2">Prescription ID</td>
                <td class="label-isalus-small">Message</td>
            </tr>
            <tr v-for="(rxRecord,index) in  formData.sentPrescriptionList">
                <td>
                    <span class="label-isalus-small pe-2">{{index + 1}}.</span>
                </td>
                <td>
                    <span class="label-isalus-small pe-2">{{rxRecord.prescriptionID}}</span>
                </td>
                <td>            
                    <span v-if="rxRecord.errorCode !== 0" class="label-isalus-small text-danger">{{rxRecord.prescriptionMessage }}</span>
                    <span v-if="rxRecord.errorCode == 0" class="label-isalus-small">{{rxRecord.prescriptionMessage }}</span> 
                </td>
            </tr>
        </table>
    </ModalDialog>

    <ModalDialog v-model:show="formData.showSignErrorPrescriptions" @hide="hideSignErrorPrescriptions">
        <template #title>
            <div class="text-isalus-primary">Prescription Signing</div>
        </template>
        <div class="label-isalus-small text-danger">
            Unable to sign queued prescriptions due to the following errors:
        </div>
        <br/>
        <table>
            <tr>
                <td class="label-isalus-small pe-2">#</td>
                <td class="label-isalus-small pe-2">Prescription ID</td>
                <td class="label-isalus-small">Error Message</td>
            </tr>
            <tr v-for="(rxRecord,index) in  formData.signPrescriptionErrorList">
                <td>
                    <span class="label-isalus-small pe-2">{{index + 1}}.</span>
                </td>
                <td>
                    <span class="label-isalus-small pe-2">{{rxRecord.prescriptionID}}</span>
                </td>
                <td>            
                    <span class="label-isalus-small text-danger">{{rxRecord.errorMessage}}</span> 
                </td>
            </tr>
        </table>
    </ModalDialog>

    <div v-if="formData.displaySigning">
        <div class="row">
            <div class="col-11">
                <span class="fw-bold fs-6 text-isalus-primary">Prescription Signing</span>
            </div>
            <div class="col-1 text-end">
                <Icon 
                    :icon="IconTypes.Close" 
                    :displayStyle="DisplayStyle.Secondary" 
                    :class="'text-isalus-primary fs-6'" 
                    :title="'Close Signing, return to Patient Queue'" 
                    @click="hideSigning"/> 
            </div>
        </div>
        <div class="row">
            <div class="col">
                <br/> 
                <hr>
                <CredentialSignForIdme
                    :epcsXmlArray="formData.signPrescriptionList" 
                    :queueSigning="formData.queueSigning"
                    @closeSigning="closeSigning"
                />
            </div>
        </div>
        <div class="row">
            <div class="col text-end">
                <br/>
                <hr>
                <ActionButtonV2
                    :action="ActionButtonStyle.Reverse"  
                    :text="'Return to Patient Queue'"  
                    :tooltip="'Close Signing, performing NO actions and return to Patient Queue'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                    :performAction="hideSigning"/> 
            </div>
        </div>
    </div>
    
    <RxPin 
        :show="formData.showPIN"
        :pinType="UserPinType.Prescription"
        @closePin="closePin"
    /> 
</template>
